/**
 * Legal document version history registry — per brand (ADR-088).
 *
 * Single source of truth for *which* version is current per (brand,
 * documentType), and for the effective period of every past version.
 * Current version numbers are derived from `LEGAL_VERSIONS_BY_BRAND`
 * (ADR-075 mechanism, ADR-088 per-brand dimension) so bumping a single date
 * in `version.ts` propagates to the landing pages, the archive routes and
 * the backend gate without touching this file.
 *
 * Publishing a new version for a brand (manual step, see AC-15 / spec
 * 2026-07-02-regulamin-reacceptance-flow, extended by ADR-088):
 *   1. copy the outgoing markdown to `content/<brand>/archive/<doc>-<version>.md`
 *   2. add a historical entry for the outgoing version with `effectiveTo` set
 *   3. bump `LEGAL_VERSIONS_BY_BRAND[brand]` in `version.ts` (current entry
 *      follows automatically)
 *   4. append the new `{ brand, doc, version, sha256 }` to `content-manifest.ts`
 *
 * Brand-agnostic consumers should go through `getLegalContent(brandSlug)`
 * (`./registry`) rather than importing from this module directly — the
 * `ForBrand`-suffixed functions here are the primitives the registry is built
 * on (H2: brandSlug is resolved ONCE, not threaded through every signature).
 * The un-suffixed functions (`currentVersion`, `isVersionCurrent`, ...) are
 * kept for backward compatibility only, and are permanently bound to
 * `kiedynabory` (the only brand that existed before ADR-088).
 */
import { LEGAL_VERSIONS_BY_BRAND } from './version';
import { BrandSlug, FALLBACK_BRAND_SLUG } from './brand';

export type LegalDocumentType = 'terms' | 'privacy';

/**
 * The document types, in a stable order for deterministic output (ADR-075 SoT).
 * Canonical source for this array — backend and frontend consumers MUST import
 * this instead of re-declaring `['terms', 'privacy']` locally (see M1,
 * docs/specifications/2026-07-02-regulamin-reacceptance-flow/reviews/consolidated.md).
 */
export const LEGAL_DOCUMENT_TYPES: readonly LegalDocumentType[] = [
  'terms',
  'privacy',
];

/**
 * Human-readable Polish label per document type. Shared verbatim by the
 * backend (campaign mail) and the frontend (`ReacceptanceModal`) — only move
 * this here if both consumers use the exact same wording; if wording ever
 * needs to diverge, revert to local copies instead of forcing one string.
 */
export const LEGAL_DOCUMENT_LABELS: Record<LegalDocumentType, string> = {
  terms: 'Regulamin',
  privacy: 'Polityka prywatności',
};

export interface LegalVersionHistoryEntry {
  brandSlug: BrandSlug;
  documentType: LegalDocumentType;
  /** Version identifier (per-brand suffixed, e.g. `2026-07-06-kn`). */
  version: string;
  /** Publication date, format `YYYY-MM-DD` — NOT the same string as `version` (which carries a brand suffix). */
  effectiveFrom: string;
  /** Date the version stopped being current, or `null` for the current one. */
  effectiveTo: string | null;
  /** Markdown path relative to the package root. */
  contentPath: string;
}

/**
 * Ordered newest-first per (brand, documentType). Exactly one current entry
 * (`effectiveTo === null`) per (brand, documentType) — see
 * `version-history.spec.ts`.
 */
export const LEGAL_VERSION_HISTORY: readonly LegalVersionHistoryEntry[] = [
  // --- kiedynabory --------------------------------------------------------
  {
    brandSlug: 'kiedynabory',
    documentType: 'terms',
    version: LEGAL_VERSIONS_BY_BRAND.kiedynabory.TERMS,
    effectiveFrom: '2026-07-09',
    effectiveTo: null,
    contentPath: 'content/kiedynabory/terms.md',
  },
  // NOTE: version `2026-07-06-kn` existed only in-repo and was never deployed
  // to production — the last version actually in force was `2026-05-20`, which
  // `2026-07-09-kn` supersedes directly. No `2026-07-06-kn` history entry: it
  // was never a published version a user could have accepted or browse.
  {
    brandSlug: 'kiedynabory',
    documentType: 'terms',
    version: '2026-05-20',
    effectiveFrom: '2026-05-20',
    effectiveTo: '2026-07-09',
    contentPath: 'content/kiedynabory/archive/terms-of-service-2026-05-20.md',
  },
  // Original launch version, in force from the service's inception until the
  // `2026-05-20` RODO overhaul superseded it. Recovered from the pre-RODO
  // embedded landing page (`regulamin.astro` @ cff15dc0c) — the document
  // itself declared "Obowiązuje od dnia: 1 stycznia 2025 r.".
  {
    brandSlug: 'kiedynabory',
    documentType: 'terms',
    version: '2025-01-01',
    effectiveFrom: '2025-01-01',
    effectiveTo: '2026-05-20',
    contentPath: 'content/kiedynabory/archive/terms-of-service-2025-01-01.md',
  },
  {
    brandSlug: 'kiedynabory',
    documentType: 'privacy',
    version: LEGAL_VERSIONS_BY_BRAND.kiedynabory.PRIVACY,
    effectiveFrom: '2026-11-03',
    effectiveTo: null,
    contentPath: 'content/kiedynabory/privacy.md',
  },
  {
    brandSlug: 'kiedynabory',
    documentType: 'privacy',
    version: '2026-07-09-kn',
    effectiveFrom: '2026-07-09',
    effectiveTo: '2026-11-03',
    contentPath: 'content/kiedynabory/archive/privacy-policy-2026-07-09-kn.md',
  },
  // NOTE: `2026-07-06-kn` was never deployed (see the terms note above) — the
  // privacy policy also went straight from `2026-05-20` to `2026-07-09-kn`.
  {
    brandSlug: 'kiedynabory',
    documentType: 'privacy',
    version: '2026-05-20',
    effectiveFrom: '2026-05-20',
    effectiveTo: '2026-07-09',
    contentPath: 'content/kiedynabory/archive/privacy-policy-2026-05-20.md',
  },
  // Original launch version — see the terms note above; the privacy policy
  // was likewise embedded in `polityka-prywatnosci.astro` (@ cff15dc0c) and
  // declared "Obowiązuje od dnia: 1 stycznia 2025 r." until `2026-05-20`.
  {
    brandSlug: 'kiedynabory',
    documentType: 'privacy',
    version: '2025-01-01',
    effectiveFrom: '2025-01-01',
    effectiveTo: '2026-05-20',
    contentPath: 'content/kiedynabory/archive/privacy-policy-2025-01-01.md',
  },
  // --- hotelb2b -------------------------------------------------------------
  // Brand-new split (ADR-088): terms have no prior version; privacy has one.
  {
    brandSlug: 'hotelb2b',
    documentType: 'terms',
    version: LEGAL_VERSIONS_BY_BRAND.hotelb2b.TERMS,
    effectiveFrom: '2026-07-06',
    effectiveTo: null,
    contentPath: 'content/hotelb2b/terms.md',
  },
  {
    brandSlug: 'hotelb2b',
    documentType: 'privacy',
    version: LEGAL_VERSIONS_BY_BRAND.hotelb2b.PRIVACY,
    effectiveFrom: '2026-11-03',
    effectiveTo: null,
    contentPath: 'content/hotelb2b/privacy.md',
  },
  {
    brandSlug: 'hotelb2b',
    documentType: 'privacy',
    version: '2026-07-06-hb',
    effectiveFrom: '2026-07-06',
    effectiveTo: '2026-11-03',
    contentPath: 'content/hotelb2b/archive/privacy-policy-2026-07-06-hb.md',
  },
];

/** All history entries for one brand (newest-first, same relative order as {@link LEGAL_VERSION_HISTORY}). */
export function historyForBrand(
  brandSlug: BrandSlug,
): LegalVersionHistoryEntry[] {
  return LEGAL_VERSION_HISTORY.filter((e) => e.brandSlug === brandSlug);
}

/** Current published version string for (brand, documentType) (from `LEGAL_VERSIONS_BY_BRAND`). */
export function currentVersionForBrand(
  brandSlug: BrandSlug,
  documentType: LegalDocumentType,
): string {
  const key = documentType === 'terms' ? 'TERMS' : 'PRIVACY';
  return LEGAL_VERSIONS_BY_BRAND[brandSlug][key];
}

/**
 * Version-currency predicate (ADR-088 SoT), per brand. Returns whether
 * `version` is the currently published version of (brand, documentType).
 * Non-throwing — for hot-path / boolean call sites.
 */
export function isVersionCurrentForBrand(
  brandSlug: BrandSlug,
  documentType: LegalDocumentType,
  version: string,
): boolean {
  return version === currentVersionForBrand(brandSlug, documentType);
}

/**
 * Thrown by {@link assertVersionIsCurrentForBrand} (and its KN-only
 * backward-compat wrapper {@link assertVersionIsCurrent}) when `version` is
 * not the currently published version of `documentType`. Carries a
 * machine-readable `code` plus expected/received versions so callers (e.g.
 * the `legal` BC's `POST /legal/accept`) can map it to their own HTTP
 * exception shape without re-deriving the comparison.
 */
export class LegalVersionMismatchError extends Error {
  readonly code = 'LEGAL_VERSION_MISMATCH' as const;

  constructor(
    public readonly documentType: LegalDocumentType,
    public readonly expectedVersion: string,
    public readonly receivedVersion: string,
  ) {
    super(
      `Legal version mismatch for "${documentType}": expected "${expectedVersion}", received "${receivedVersion}"`,
    );
    this.name = 'LegalVersionMismatchError';
  }
}

/**
 * Version-currency assertion (ADR-088 SoT), per brand. Throws
 * {@link LegalVersionMismatchError} unless `version` is the currently
 * published version of (brand, documentType).
 */
export function assertVersionIsCurrentForBrand(
  brandSlug: BrandSlug,
  documentType: LegalDocumentType,
  version: string,
): void {
  if (!isVersionCurrentForBrand(brandSlug, documentType, version)) {
    throw new LegalVersionMismatchError(
      documentType,
      currentVersionForBrand(brandSlug, documentType),
      version,
    );
  }
}

/** The current (in-force) history entry for (brand, documentType). */
export function getCurrentVersionEntryForBrand(
  brandSlug: BrandSlug,
  documentType: LegalDocumentType,
): LegalVersionHistoryEntry {
  const entry = LEGAL_VERSION_HISTORY.find(
    (e) =>
      e.brandSlug === brandSlug &&
      e.documentType === documentType &&
      e.effectiveTo === null,
  );
  if (!entry) {
    throw new Error(
      `No current legal version entry for brand "${brandSlug}", document type "${documentType}"`,
    );
  }
  return entry;
}

// ---------------------------------------------------------------------------
// Backward compat (pre-ADR-088) — permanently bound to `kiedynabory`. New
// code should use `getLegalContent(brandSlug)` (`./registry`) instead.
// ---------------------------------------------------------------------------

/** @deprecated KN-only backward compat — use `getLegalContent(brand).currentVersion(docType)`. */
export function currentVersion(documentType: LegalDocumentType): string {
  return currentVersionForBrand(FALLBACK_BRAND_SLUG, documentType);
}

/** @deprecated KN-only backward compat — use `getLegalContent(brand).isVersionCurrent(docType, version)`. */
export function isVersionCurrent(
  documentType: LegalDocumentType,
  version: string,
): boolean {
  return isVersionCurrentForBrand(FALLBACK_BRAND_SLUG, documentType, version);
}

/** @deprecated KN-only backward compat. */
export function assertVersionIsCurrent(
  documentType: LegalDocumentType,
  version: string,
): void {
  assertVersionIsCurrentForBrand(FALLBACK_BRAND_SLUG, documentType, version);
}

/** @deprecated KN-only backward compat — use `getLegalContent(brand).getCurrentVersionEntry(docType)`. */
export function getCurrentVersionEntry(
  documentType: LegalDocumentType,
): LegalVersionHistoryEntry {
  return getCurrentVersionEntryForBrand(FALLBACK_BRAND_SLUG, documentType);
}

function toIsoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/**
 * Pure selector behind {@link getArchivedVersions}, parameterised for testing.
 * An entry qualifies as an archived (browsable) version only when it is both
 * superseded (`effectiveTo !== null`) and already published (`effectiveFrom`
 * not in the future) — so future/unpublished versions never get a page.
 * `brandSlug` is an optional additional filter (ADR-088) — omitted means "any
 * brand", so a brand-specific page MUST pass it (both brands have archived
 * entries).
 */
export function selectArchivedVersions(
  history: readonly LegalVersionHistoryEntry[],
  options: {
    documentType?: LegalDocumentType;
    brandSlug?: BrandSlug;
    now?: Date;
  } = {},
): LegalVersionHistoryEntry[] {
  const today = toIsoDate(options.now ?? new Date());
  return history.filter(
    (e) =>
      e.effectiveTo !== null &&
      e.effectiveFrom <= today &&
      (options.documentType === undefined ||
        e.documentType === options.documentType) &&
      (options.brandSlug === undefined || e.brandSlug === options.brandSlug),
  );
}

/**
 * Archived (superseded, already-published) versions, newest-first. Drives Astro
 * `getStaticPaths` for `/regulamin/<wersja>/` and `/polityka-prywatnosci/<wersja>/`.
 */
export function getArchivedVersions(
  documentType?: LegalDocumentType,
  brandSlug?: BrandSlug,
): LegalVersionHistoryEntry[] {
  return selectArchivedVersions(LEGAL_VERSION_HISTORY, {
    documentType,
    brandSlug,
  });
}

const PL_MONTHS_GENITIVE = [
  'stycznia',
  'lutego',
  'marca',
  'kwietnia',
  'maja',
  'czerwca',
  'lipca',
  'sierpnia',
  'września',
  'października',
  'listopada',
  'grudnia',
];

/** Formats an ISO `YYYY-MM-DD` date as Polish long form, e.g. `2 lipca 2026 r.` */
export function formatLegalDatePl(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  const monthName = PL_MONTHS_GENITIVE[month - 1];
  if (!monthName || Number.isNaN(year) || Number.isNaN(day)) {
    return iso;
  }
  return `${day} ${monthName} ${year} r.`;
}
